import {
  integrationsApi,
  useGetCurrentUserQuery,
  useGetPlaybookQuery,
} from '@carboncopyco/data';
import {
  PlayIcon,
  ShareOutlineIcon,
  ShowOutlineIcon,
} from '@carboncopyco/icons';
import type { Play, Role } from '@carboncopyco/types';
import { Button, Separator } from '@carboncopyco/ui';
import {
  AnalyticsTrackEventName,
  isDesktop,
  useAnalytics,
  usePlaybookAnalytics,
} from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DemoLinkPlaybookModal } from '../../DemoLinkModal';
import { GiveADemoFlowModal } from '../../PresentationModals';
import {
  PlaybookGiveADemoTooltip,
  PlaybookPreviewTooltip,
} from '../../Tooltip';
import { useFilesServiceContext } from '../../utils';
import { usePlaybookPageProps } from './PlaybookPageContext';
import { PlaybookPageDropdown } from './PlaybookPageDropdown';
import { usePresentationsActions } from './PresentationsContext';

export const PlaybookActionButtons = () => {
  const { trackEvent } = useAnalytics();
  const [
    showGiveADemoModal,
    { setTrue: setShowGiveADemoModal, setFalse: setHideGiveADemoModal },
  ] = useBoolean();
  const [openDemoLinkModal, setOpenDemoLinkModal] = useState(false);
  const {
    playbookId,
    organizationId,
    rootPath,
    playPresentation,
    desktopDomain,
    workspaceId,
    readOnly,
    goToPlaybookPreview,
  } = usePlaybookPageProps();
  const { trackPlaybookClickPresent, trackPlaybookClickShareInternally } =
    usePlaybookAnalytics('give_a_demo');
  const { filesServiceDomain } = useFilesServiceContext();
  const giveADemoRef = useRef(null);
  const previewRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const flags = useFlags();
  const prefetchIntegrations = integrationsApi.usePrefetch('getIntegrations');

  useEffect(() => {
    prefetchIntegrations({
      organizationId: Number(organizationId),
    });
  }, [organizationId, prefetchIntegrations]);

  const { data: userData } = useGetCurrentUserQuery();
  const userRole: Role = userData?.user?.roles?.[organizationId];
  const navigate = useNavigate();

  const { data: playbook } = useGetPlaybookQuery({
    id: playbookId,
    workspaceId,
    organizationId,
  });

  useEffect(() => {
    if (playbook?.name && searchParams.get('giveADemo') && isDesktop()) {
      setShowGiveADemoModal();
      setSearchParams();
    }
  }, [playbook, searchParams, setSearchParams, setShowGiveADemoModal]);

  const { addItem: addNewPresentation } = usePresentationsActions();

  const handleOnPresent = async (play: Play) => {
    addNewPresentation(play);
    setHideGiveADemoModal();
    trackPlaybookClickPresent({
      playbookId: play.asset.assetId,
    });
    await playPresentation(play);
  };

  return (
    <div className="flex items-center gap-3">
      {readOnly ? (
        <>
          <Button
            secondary
            ref={previewRef}
            onClick={() => {
              trackEvent(AnalyticsTrackEventName.PLAYBOOK_CLICK_PREVIEW, {
                location: 'playbook_info_page_top_actions',
              });
              goToPlaybookPreview?.();
            }}
            leftIcon={<ShowOutlineIcon />}
            label="Preview"
          />
          <PlaybookPreviewTooltip
            desktopDomain={desktopDomain}
            previewRef={previewRef}
            location={location}
          />
        </>
      ) : (
        <PlaybookPageDropdown />
      )}
      <div className="h-7 pr-2">
        <Separator />
      </div>
      {!readOnly && flags.demoLink && (
        <Button
          secondary
          label="Share"
          onClick={() => {
            trackPlaybookClickShareInternally();
            setOpenDemoLinkModal(true);
          }}
          leftIcon={<ShareOutlineIcon />}
        />
      )}

      <Button
        ref={giveADemoRef}
        onClick={() => {
          setShowGiveADemoModal();
        }}
        leftIcon={<PlayIcon />}
        label="Give a demo"
      />
      <PlaybookGiveADemoTooltip
        desktopDomain={desktopDomain}
        giveADemoRef={giveADemoRef}
        location={location}
      />
      {openDemoLinkModal && (
        <DemoLinkPlaybookModal
          workspaceId={workspaceId}
          assetId={playbookId}
          assetType="playbook"
          organizationId={organizationId}
          isOpen={openDemoLinkModal}
          onClose={() => setOpenDemoLinkModal(false)}
          role={userRole}
        />
      )}
      {showGiveADemoModal && playbook && (
        <GiveADemoFlowModal
          workspaceId={workspaceId}
          open={showGiveADemoModal}
          asset={{
            name: playbook.name,
            id: playbook.id,
            type: 'playbook',
            assets: playbook.assets,
          }}
          onClose={setHideGiveADemoModal}
          onPresent={handleOnPresent}
          onSave={(play) => {
            navigate(`${rootPath}/presentations`, {
              state: { play },
            });
          }}
          filesServiceUrl={filesServiceDomain}
        />
      )}
    </div>
  );
};
